import React from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { SearchInput } from './SearchInput';
import { ReferralItem } from './ReferralItem';
import { useTheme } from '@/hooks/useTheme';
import { getStatusLabel } from '@/utils/referralStatus';
import type { ReferralsSectionProps, ReferralStatus } from '@/types/dashboard';
import { cn } from '@/utils/cn';

export const ReferralsSection: React.FC<ReferralsSectionProps> = ({
  referrals,
  photoUrls,
  filterState,
  onFilterChange,
  onReferralClick,
  loading = false,
  error = null,
  className,
}) => {
  const { getTextColorClass, getBorderClass, getThemeClasses } = useTheme();
  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: getStatusLabel('pending') },
    { value: 'claimed', label: getStatusLabel('claimed') },
    { value: 'scheduled', label: getStatusLabel('scheduled') },
    { value: 'no_show', label: getStatusLabel('no_show') },
    { value: 'awaiting_mealplan', label: getStatusLabel('awaiting_mealplan') },
    { value: 'expired', label: getStatusLabel('expired') },
    { value: 'cancelled', label: getStatusLabel('cancelled') },
    { value: 'complete', label: getStatusLabel('complete') },
  ];

  const handleStatusFilterChange = (value: string) => {
    onFilterChange({ statusFilter: value as ReferralStatus | 'all' });
  };

  return (
    <Card className={cn('overflow-hidden', className)}>
      {/* Header */}
      <div className={cn("p-6 border-b", getBorderClass())}>
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h2 className={cn("text-xl font-semibold mb-1", getTextColorClass('primary'))}>
              Recent Referrals
            </h2>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full lg:w-auto">
            <SearchInput
              value={filterState.searchTerm}
              onChange={(value) => onFilterChange({ searchTerm: value })}
              placeholder="Search referrals..."
              className="w-40 h-10"
            />

            <Select
              value={filterState.statusFilter}
              onValueChange={handleStatusFilterChange}
              options={statusOptions}
              placeholder="Filter by status"
              className="w-36 h-10"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {error && (
          <div className={cn(
            "mb-4 p-4 rounded-lg",
            getThemeClasses(
              'bg-red-50 border border-red-200',
              'bg-red-900/20 border border-red-800'
            )
          )}>
            <p className={cn(
              "text-sm",
              getThemeClasses('text-red-800', 'text-red-200')
            )}>
              {error}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.reload()}
              className="mt-2"
            >
              Try Again
            </Button>
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, index) => (
              <div
                key={index}
                className={cn(
                  "flex items-center gap-4 p-4 rounded-lg animate-pulse border",
                  getBorderClass()
                )}
              >
                <div className={cn(
                  "w-10 h-10 rounded-full",
                  getThemeClasses('bg-gray-200', 'bg-gray-700')
                )} />
                <div className="flex-1 space-y-2">
                  <div className={cn(
                    "h-4 rounded w-1/3",
                    getThemeClasses('bg-gray-200', 'bg-gray-700')
                  )} />
                  <div className={cn(
                    "h-3 rounded w-1/2",
                    getThemeClasses('bg-gray-200', 'bg-gray-700')
                  )} />
                </div>
                <div className={cn(
                  "w-20 h-6 rounded",
                  getThemeClasses('bg-gray-200', 'bg-gray-700')
                )} />
              </div>
            ))}
          </div>
        ) : referrals.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4" aria-hidden="true">📋</div>
            <h3 className={cn("text-lg font-medium mb-2", getTextColorClass('primary'))}>
              No referrals found
            </h3>
            <p className={getTextColorClass('secondary')}>
              {filterState.searchTerm || filterState.statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'You haven\'t made any referrals yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="referrals-table">
            {referrals.map((referral) => (
              <ReferralItem
                key={referral.id}
                referral={referral}
                photoUrl={photoUrls[referral.claimedByUserId || ''] || null}
                onClick={onReferralClick}
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};